// Newton's method for f(z) = z^3 - 1, whose three roots are the cube roots
// of unity. Four-slot fill-in (see README.md) — the one variant where the
// template's exit condition changes meaning:
//   initial_state(p) = p      parameter: none      step = z - f(z)/f'(z)
//   escaped(z) = |z - root_k| < eps for some known root  (arrival, not divergence)

@group(0) @binding(0) var<storage, read_write> output: array<u32>;

struct Params {
  width:      u32,
  height:     u32,
  center_re:  f32,
  center_im:  f32,
  scale:      f32,
  max_iter:   u32,
}
@group(0) @binding(1) var<uniform> params: Params;

const EPSILON2: f32 = 1e-12; // |z - root|^2 < EPSILON2  <=>  |z - root| < 1e-6

fn root(i: u32) -> Complex {
  switch i {
    case 0u:  { return Complex(1.0, 0.0); }
    case 1u:  { return Complex(-0.5, 0.8660254); }
    default:  { return Complex(-0.5, -0.8660254); }
  }
}

fn newtonStep(z: Complex) -> Complex {
  let z2 = cMul(z, z);
  let z3 = cMul(z2, z);
  let fz = Complex(z3.re - 1.0, z3.im); // f(z)  = z^3 - 1
  let fpz = cScale(z2, 3.0);            // f'(z) = 3z^2
  return cSub(z, cDiv(fz, fpz));
}

@compute @workgroup_size(8, 8)
fn main(@builtin(global_invocation_id) gid: vec3u) {
  if (gid.x >= params.width || gid.y >= params.height) { return; }

  var z = pixelToComplex(gid.x, gid.y, params.width, params.height, params.center_re, params.center_im, params.scale);

  var iter: u32 = 0u;
  var converged_root: u32 = 3u; // sentinel: no root matched
  loop {
    if (iter >= params.max_iter) { break; }
    z = newtonStep(z);

    var matched = false;
    for (var k: u32 = 0u; k < 3u; k = k + 1u) {
      if (cAbs2(cSub(z, root(k))) < EPSILON2) {
        converged_root = k;
        matched = true;
        break;
      }
    }
    if (matched) { break; }
    iter = iter + 1u;
  }

  output[gid.y * params.width + gid.x] = colorizeRoot(converged_root, iter, params.max_iter);
}
